import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaClipboardCheck, FaEllipsisV, FaExchangeAlt, FaExclamationTriangle, FaExternalLinkAlt, FaHistory, FaHourglassHalf, FaInbox, FaSearch, FaTasks, FaTimes } from 'react-icons/fa';
import ErpTemplate from '../../components/ErpTemplate';
import Data from '../../components/Data';
import ReactSelect from '../../components/ReactSelect';
import ObservacaoAnexos from '../../components/ObservacaoAnexos';
import { FinCheck, FinChecks, FinField, FinInput } from '../../components/FinField';
import { useSetFullBreadcrumbTrail } from '../../context/BreadcrumbContext';
import TabelaLoading from '../../components/TabelaLoading';
import TabelaVazia from '../../components/TabelaVazia';
import api from '../../services/api';
import notify from '../../services/notify';
import '../../css/financeiro-parcela-pagar.css';

const API = process.env.REACT_APP_API_URL;
const DIA = 'DD/MM/YYYY';

// O tom da pill de cada status do Backoffice (os nomes vêm da API).
const TOM_DO_STATUS = {
    stand_by: 'is-muted',
    aguardando: 'is-muted',
    pendente_bo: 'is-info',
    pendente_eventos: 'is-danger',
    em_andamento: 'is-info',
    em_andamento_pendencia: 'is-warn',
    finalizado: 'is-ok',
};

// De onde veio cada entrada do histórico.
const ORIGEM = {
    painel: 'pelo painel',
    etapa: 'no registro da etapa',
    email: 'automático, pela aba de Fechamento',
    fechamento: 'no Fechar do Backoffice',
};

// As situações do evento (`evento.situacao`), como no Painel Aéreo; cancelado fica fora do painel.
const SITUACOES = [
    { value: 'ativo', label: 'Aberto', tom: 'is-ok' },
    { value: 'fechado', label: 'Fechado', tom: 'is-info' },
    { value: 'pendente', label: 'Pendente', tom: 'is-warn' },
];
const SITUACOES_PADRAO = ['ativo', 'pendente'];
// "Todos" no select de situação: no estado é a lista vazia, que o back lê como sem filtro.
const TODAS_SITUACOES = { value: 'todos', label: 'Todos' };
const ESTILO_SITUACOES = {
    multiValueLabel: (base, { data }) => (data.value === TODAS_SITUACOES.value ? { ...base, paddingRight: 6 } : base),
    multiValueRemove: (base, { data }) => (data.value === TODAS_SITUACOES.value ? { ...base, display: 'none' } : base),
};
const situacoesEscolhidas = (sel) => {
    const valores = (sel || []).map((s) => s.value);
    // Escolher o "Todos" limpa as outras; escolher uma situação tira o "Todos".
    if (valores[valores.length - 1] === TODAS_SITUACOES.value) return [];
    const situacoes = valores.filter((v) => v !== TODAS_SITUACOES.value);
    return situacoes.length === SITUACOES.length ? [] : situacoes;
};

// Os cards do topo: cada um conta um ou dois status e, clicado, filtra a tabela por eles. No card de dois status,
// cada número filtra só o seu. `cor`: a borda do card ativo; `cores`: a de cada número.
const CARDS = [
    { chave: 'espera', rotulo: 'Stand-by · Aguardando', status: ['stand_by', 'aguardando'], icone: FaHourglassHalf, ic: '',
        cor: 'var(--fin-mt)', cores: ['var(--fin-ft)', 'var(--fin-mt)'], sub: 'antes e depois do evento, sem os docs' },
    { chave: 'pendente_bo', rotulo: 'Pendente BO', status: ['pendente_bo'], icone: FaInbox, ic: 'is-info',
        cor: 'var(--fin-ac)', valor: 'is-info', sub: 'docs recebidos, com o Backoffice' },
    { chave: 'pendente_eventos', rotulo: 'Pendente Eventos', status: ['pendente_eventos'], icone: FaExclamationTriangle, ic: 'is-danger',
        cor: 'var(--fin-dg)', valor: 'is-danger', sub: 'algum doc faltando' },
    { chave: 'andamento', rotulo: 'Em andamento · Com pendência', status: ['em_andamento', 'em_andamento_pendencia'], icone: FaTasks, ic: 'is-info',
        cor: 'var(--fin-ac)', cores: ['var(--fin-ac)', 'var(--fin-wn)'], sub: 'o Backoffice trabalhando; com divergência' },
    { chave: 'finalizado', rotulo: 'Finalizados', status: ['finalizado'], icone: FaCheckCircle, ic: 'is-ok',
        cor: 'var(--fin-ok)', valor: 'is-ok', sub: 'status Finalizado' },
];

// O filtro de status é exatamente esta lista (a ordem não importa)?
const mesmaLista = (a, b) => a.length === b.length && a.every((s) => b.includes(s));

const quando = (data) => (data ? moment(data).format('DD/MM/YYYY HH:mm') : '');
const periodo = (e) => {
    const inicio = e.data ? moment(e.data).format(DIA) : '';
    const fim = e.dataFim ? moment(e.dataFim).format(DIA) : '';
    return fim && fim !== inicio ? `${inicio} a ${fim}` : inicio;
};
// A data do filtro em Y-m-d, ou vazio enquanto não for uma data inteira.
const paraApi = (data) => {
    const m = moment(data, DIA, true);
    return m.isValid() ? m.format('YYYY-MM-DD') : '';
};

function PillStatus({ status }) {
    if (!status) return null;
    return <span className={'fin-pp__pill ' + (TOM_DO_STATUS[status.status] || 'is-muted')}>{status.rotulo}</span>;
}

/**
 * Painel do Backoffice (`/backoffice`), a demanda do Backoffice como o Painel Aéreo é a do Aéreo: os eventos de todo
 * cliente com o status do Backoffice, o histórico das mudanças e as etapas de finalização. Só o Backoffice e o admin.
 * Sem entrada nenhuma, o status sai do evento: Finalizado com o Fechar do Backoffice feito, Aguardando depois do
 * evento, Stand-by antes.
 */
export default function PainelBackoffice() {
    const token = localStorage.getItem('token');
    const perfil = localStorage.getItem('perfil');
    const [texto, setTexto] = useState('');
    const [buscar, setBuscar] = useState('');
    const [status, setStatus] = useState([]);
    const [situacoes, setSituacoes] = useState(SITUACOES_PADRAO);
    // O período começa nos últimos 3 meses: sem ele, todo evento antigo aparece em Aguardando.
    const [de, setDe] = useState(moment().subtract(3, 'months').format(DIA));
    const [ate, setAte] = useState('');
    const [ordem, setOrdem] = useState('data:desc');
    const [pagina, setPagina] = useState({ page: 1, pageSize: 20 });
    const [dados, setDados] = useState({ eventos: [], status: {}, contagens: {}, total: 0, page: 1, lastPage: 1 });
    const [carregando, setCarregando] = useState(true);
    const [recarga, setRecarga] = useState(0);
    const [acoesDe, setAcoesDe] = useState(null);
    const [mudarDe, setMudarDe] = useState(null);
    const [historicoDe, setHistoricoDe] = useState(null);
    const buscaId = useRef(0);

    const recarregar = useCallback(() => setRecarga((n) => n + 1), []);
    const deApi = paraApi(de);
    const ateApi = paraApi(ate);

    const setFullTrail = useSetFullBreadcrumbTrail();
    useEffect(() => {
        setFullTrail([{ path: '/backoffice', label: 'Backoffice' }]);
    }, [setFullTrail]);

    useEffect(() => {
        const id = ++buscaId.current;
        setCarregando(true);
        api({
            method: 'get', action: '/eventoBackofficePainel', token,
            params: { status, situacao: situacoes, buscar, de: deApi, ate: ateApi, ordem, page: pagina.page, pageSize: pagina.pageSize },
        })
            .then((res) => {
                if (id !== buscaId.current) return;
                const r = res.data || {};
                setDados({
                    eventos: r.data ?? [], status: r.status ?? {}, contagens: r.contagens ?? {},
                    total: r.total ?? 0, page: r.page ?? 1, lastPage: r.lastPage ?? 1,
                });
            })
            .catch((err) => {
                if (id === buscaId.current) notify.apiError(err, 'Erro ao carregar o painel do Backoffice');
            })
            .finally(() => {
                if (id === buscaId.current) setCarregando(false);
            });
    }, [token, status, situacoes, buscar, deApi, ateApi, ordem, pagina, recarga]);

    // Filtro novo volta para a primeira página.
    const primeiraPagina = () => setPagina((p) => ({ ...p, page: 1 }));
    const aplicarBusca = () => {
        setBuscar(texto.trim());
        primeiraPagina();
    };
    const opcoesStatus = Object.entries(dados.status).map(([value, label]) => ({ value, label }));
    // O clique num card ou num número dele: filtra pelos status dele, no lugar do filtro de agora; de novo, tira.
    const filtrarPorCard = (lista) => {
        setStatus((atual) => (mesmaLista(atual, lista) ? [] : lista));
        primeiraPagina();
    };
    const filtrosMexidos = texto !== '' || buscar !== '' || status.length > 0 || ate !== ''
        || de !== moment().subtract(3, 'months').format(DIA)
        || [...situacoes].sort().join() !== [...SITUACOES_PADRAO].sort().join();
    const limparFiltros = () => {
        setTexto('');
        setBuscar('');
        setStatus([]);
        setSituacoes(SITUACOES_PADRAO);
        setDe(moment().subtract(3, 'months').format(DIA));
        setAte('');
        primeiraPagina();
    };

    const [colunaOrdem, direcaoOrdem] = ordem.split(':');
    const ordenar = (coluna) => {
        setOrdem(colunaOrdem === coluna && direcaoOrdem === 'desc' ? `${coluna}:asc` : `${coluna}:desc`);
        primeiraPagina();
    };
    const thOrdenavel = (label, coluna, style) => (
        <th className="is-sortable" style={style} onClick={() => ordenar(coluna)}>
            {label}{colunaOrdem === coluna ? (direcaoOrdem === 'asc' ? ' ▲' : ' ▼') : ''}
        </th>
    );

    const totalPaginas = Math.max(1, dados.lastPage || 1);
    const p = dados.page || 1;

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Backoffice</h1>
                        <p className="fin-pp__subtitle">Painel do Backoffice · o status da demanda em cada evento.</p>
                    </div>
                </div>

                {/* ── Cards: os eventos de cada status, com os filtros da tabela menos o de status. O clique filtra a
                    tabela pelos status do card; no card de dois status, cada número filtra só o seu. ──── */}
                <section className="fin-pp__kpis" style={{ marginBottom: 18 }}>
                    {CARDS.map((card) => {
                        // Ativo quando o filtro de agora é o do card ou um dos status dele.
                        const ativo = status.length > 0 && status.every((s) => card.status.includes(s));
                        const Icone = card.icone;
                        const rotulos = card.status.map((s) => dados.status[s]).filter(Boolean).join(' e ');
                        return (
                            <div key={card.chave} className="fin-pp__kpi" role="button" tabIndex={0} aria-pressed={ativo}
                                title={ativo && mesmaLista(status, card.status) ? 'Clique para tirar o filtro' : `Filtrar: ${rotulos}`}
                                onClick={() => filtrarPorCard(card.status)}
                                onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); filtrarPorCard(card.status); } }}
                                style={{ cursor: 'pointer', ...(ativo ? { borderColor: card.cor, boxShadow: `0 0 0 1px ${card.cor}` } : {}) }}>
                                <div className="fin-pp__kpi-top">
                                    <span className="fin-pp__kpi-label">{card.rotulo}</span>
                                    <span className={'fin-pp__kpi-ic ' + card.ic}><Icone size={14} /></span>
                                </div>
                                {card.cores ? (
                                    <div className="fin-pp__kpi-value">
                                        {card.status.map((codigo, i) => (
                                            <React.Fragment key={codigo}>
                                                {i > 0 && <span style={{ color: 'var(--fin-ft)', fontWeight: 400, margin: '0 8px' }}>·</span>}
                                                <span title={`Filtrar só ${dados.status[codigo] || codigo}`}
                                                    onClick={(ev) => { ev.stopPropagation(); filtrarPorCard([codigo]); }}
                                                    style={{ color: card.cores[i], textDecoration: mesmaLista(status, [codigo]) ? 'underline' : undefined }}>
                                                    {dados.contagens[codigo] ?? 0}
                                                </span>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                ) : (
                                    <div className={'fin-pp__kpi-value ' + card.valor}>{dados.contagens[card.status[0]] ?? 0}</div>
                                )}
                                <div className="fin-pp__kpi-sub">{card.sub}</div>
                            </div>
                        );
                    })}
                </section>

                {/* ── Eventos ──────────────────────────────────────────────── */}
                <div className="fin-pp__region">
                    <div className="fin-pp__cphead">
                        <span className="fin-pp__cphead-ic"><FaClipboardCheck size={18} /></span>
                        <div style={{ minWidth: 0 }}>
                            <h2 className="fin-pp__cphead-title">Eventos</h2>
                            <p className="fin-pp__cphead-sub">Sem mudança registrada, o status sai do evento: Finalizado com o Fechar do Backoffice feito, Aguardando depois do evento, Stand-by antes.</p>
                        </div>
                    </div>
                    <div className="fin-pp__region-body">
                        <div className="fin-pp__card mb-3" style={{ padding: 16 }}>
                            <div className="fin-pp__filters">
                                <FinField label="Evento ou cliente" grow>
                                    <FinInput value={texto} onChange={(ev) => setTexto(ev.target.value)}
                                        onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.preventDefault(); aplicarBusca(); } }}
                                        placeholder="Nome, código, identificação ou cliente" />
                                </FinField>
                                <FinField label="Situação" style={{ flex: '1 1 240px' }}>
                                    <ReactSelect isMulti options={[TODAS_SITUACOES, ...SITUACOES]} placeholder="Todos"
                                        isClearable={situacoes.length > 0} styles={ESTILO_SITUACOES}
                                        value={situacoes.length ? SITUACOES.filter((s) => situacoes.includes(s.value)) : [TODAS_SITUACOES]}
                                        onChange={(sel) => { setSituacoes(situacoesEscolhidas(sel)); primeiraPagina(); }} />
                                </FinField>
                                <FinField label="Status do Backoffice" style={{ flex: '1 1 260px' }}>
                                    <ReactSelect isMulti options={opcoesStatus} placeholder="Todos"
                                        value={opcoesStatus.filter((o) => status.includes(o.value))}
                                        onChange={(sel) => { setStatus((sel || []).map((o) => o.value)); primeiraPagina(); }} />
                                </FinField>
                                <Data label="Evento a partir de" value={de} onChange={(v) => { setDe(v || ''); primeiraPagina(); }} />
                                <Data label="Até" value={ate} onChange={(v) => { setAte(v || ''); primeiraPagina(); }} />
                                <div className="fin-pp__filters-actions">
                                    {filtrosMexidos && (
                                        <button type="button" className="fin-pp__btn" title="Limpar filtros" onClick={limparFiltros}>
                                            <FaTimes size={13} /> Limpar
                                        </button>
                                    )}
                                    <button type="button" className="fin-pp__btn fin-pp__btn--primary" onClick={aplicarBusca}>
                                        <FaSearch size={13} /> Buscar
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            <div className="fin-pp__tblwrap">
                                <table className="fin-pp__tbl" style={{ minWidth: 1000, tableLayout: 'auto' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: 56 }}>Ações</th>
                                            {thOrdenavel('Evento', 'codigo')}
                                            {thOrdenavel('Data', 'data', { width: 180 })}
                                            <th style={{ width: 110 }}>Situação</th>
                                            <th style={{ width: 260 }}>Status do Backoffice</th>
                                            <th style={{ width: 280 }}>Etapas</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {carregando ? (
                                            <tr><td colSpan={6} className="fin-pp__empty" style={{ padding: 0 }}><TabelaLoading /></td></tr>
                                        ) : dados.eventos.length === 0 ? (
                                            <tr><td colSpan={6} className="fin-pp__empty" style={{ padding: 0 }}><TabelaVazia texto="Nenhum evento neste filtro" /></td></tr>
                                        ) : dados.eventos.map((e) => (
                                            <tr key={e.id}>
                                                <td>
                                                    <button type="button" className="fin-pp__rowbtn" title="Ações do evento" onClick={() => setAcoesDe(e)}>
                                                        <FaEllipsisV size={14} />
                                                    </button>
                                                </td>
                                                <td>
                                                    <div className="fin-pp__cell-strong">{e.nome}</div>
                                                    <div className="fin-pp__cell-muted" style={{ fontSize: 12 }}>
                                                        {[e.codigo, e.identificacao, e.cliente?.nome].filter(Boolean).join(' · ')}
                                                    </div>
                                                </td>
                                                <td className="fin-pp__cell-muted">{periodo(e) || '—'}</td>
                                                <td>
                                                    {(() => {
                                                        const situacao = SITUACOES.find((s) => s.value === String(e.situacao ?? '').toLowerCase());
                                                        return situacao
                                                            ? <span className={'fin-pp__pill ' + situacao.tom}>{situacao.label}</span>
                                                            : <span className="fin-pp__cell-muted">{e.situacao || '—'}</span>;
                                                    })()}
                                                </td>
                                                <td>
                                                    <PillStatus status={e.status} />
                                                    <div className="fin-pp__cell-muted" style={{ fontSize: 12, marginTop: 4 }}>
                                                        {e.status?.derivado
                                                            ? 'Sem mudança registrada'
                                                            : `${e.status?.registrado_por?.nome || '-'} em ${quando(e.status?.registrado_em)}`}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {(e.etapas || []).map((etapa) => (
                                                            <span key={etapa.tipo} className={'fin-pp__minitag ' + (etapa.finalizado_em ? 'is-ok' : '')}
                                                                title={etapa.finalizado_em ? `Feita em ${moment(etapa.finalizado_em).format(DIA)}` : 'Ainda não registrada'}>
                                                                {etapa.rotulo}{etapa.finalizado_em ? ` · ${moment(etapa.finalizado_em).format('DD/MM')}` : ''}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {dados.total > 0 && (
                                <div className="fin-pp__pagination">
                                    <div className="fin-pp__pagination-info">
                                        {((p - 1) * pagina.pageSize) + 1}–{Math.min(p * pagina.pageSize, dados.total)} de {dados.total}
                                    </div>
                                    <div className="fin-pp__pagination-ctrls">
                                        <select className="fin-pp__select" style={{ height: 34 }} value={pagina.pageSize}
                                            onChange={(ev) => setPagina({ page: 1, pageSize: Number(ev.target.value) })}>
                                            {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n} / pág.</option>)}
                                        </select>
                                        <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={p <= 1}
                                            onClick={() => setPagina((x) => ({ ...x, page: p - 1 }))}>Anterior</button>
                                        <span className="fin-pp__pagination-info">{p} / {totalPaginas}</span>
                                        <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={p >= totalPaginas}
                                            onClick={() => setPagina((x) => ({ ...x, page: p + 1 }))}>Próxima</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {acoesDe && (
                    <div className="fin-pp fin-pp__drawer-backdrop" onClick={() => setAcoesDe(null)}>
                        <div className="fin-pp__drawer" onClick={(ev) => ev.stopPropagation()}>
                            <div className="fin-pp__drawer-head">
                                <span className="fin-pp__drawer-head-ic"><FaEllipsisV size={16} /></span>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <h3 className="fin-pp__drawer-title">Ações do evento</h3>
                                    <p className="fin-pp__drawer-sub">{acoesDe.nome}</p>
                                </div>
                                <button type="button" className="fin-pp__drawer-close" onClick={() => setAcoesDe(null)}><FaTimes size={16} /></button>
                            </div>
                            <div className="fin-pp__drawer-body">
                                <div className="fin-pp__drawer-section">Status do Backoffice</div>
                                <button type="button" className="fin-pp__drawer-item" onClick={() => { setMudarDe(acoesDe); setAcoesDe(null); }}>
                                    <span className="fin-pp__drawer-item-ic"><FaExchangeAlt size={16} /></span>
                                    <span className="fin-pp__drawer-item-label">Mudar status</span>
                                </button>
                                <button type="button" className="fin-pp__drawer-item" onClick={() => { setHistoricoDe(acoesDe); setAcoesDe(null); }}>
                                    <span className="fin-pp__drawer-item-ic"><FaHistory size={16} /></span>
                                    <span className="fin-pp__drawer-item-label">Histórico</span>
                                </button>
                                <div className="fin-pp__drawer-section">Evento</div>
                                <Link to={`/cadastroEvento/${acoesDe.id}`} className="fin-pp__drawer-item" onClick={() => setAcoesDe(null)}>
                                    <span className="fin-pp__drawer-item-ic"><FaExternalLinkAlt size={15} /></span>
                                    <span className="fin-pp__drawer-item-label">Abrir o cadastro do evento</span>
                                </Link>
                            </div>
                        </div>
                    </div>
                )}

                {mudarDe && (
                    <MudarStatusModal evento={mudarDe} token={token} perfil={perfil}
                        onFechar={() => setMudarDe(null)}
                        onGravado={() => { setMudarDe(null); recarregar(); }} />
                )}

                {historicoDe && (
                    <HistoricoStatusModal evento={historicoDe} token={token} onFechar={() => setHistoricoDe(null)} />
                )}
            </div>
        </ErpTemplate>
    );
}

/**
 * Mudar o status do Backoffice de um evento: a escolha do status, a observação com os anexos (obrigatória em Pendente
 * Eventos e em Em andamento / Com Pendência) e, em Finalizado, o check que sugere o Fechar do Backoffice. O Fechar
 * é outro fato, com a trava de NF dele: se ele recusar, o status fica gravado e o aviso diz por quê.
 */
function MudarStatusModal({ evento, token, perfil, onFechar, onGravado }) {
    const [historico, setHistorico] = useState(null);
    const [escolhido, setEscolhido] = useState('');
    const [obs, setObs] = useState({ texto: '', anexos: [] });
    const [fecharTambem, setFecharTambem] = useState(false);
    const [gravando, setGravando] = useState(false);

    useEffect(() => {
        api({ method: 'get', action: '/eventoBackofficeStatus', token, params: { evento: evento.id } })
            .then((res) => setHistorico(res.data?.data ?? null))
            .catch((err) => notify.apiError(err, 'Erro ao carregar o status do Backoffice'));
    }, [evento.id, token]);

    const exigeObservacao = !!historico && (historico.exigemObservacao || []).includes(escolhido);
    // O Fechar é do departamento de quem está logado: só o Backoffice fecha a parte do Backoffice.
    const sugereFechar = escolhido === 'finalizado' && perfil === 'backoffice' && !historico?.evento?.backoffice_fechado_em;
    const falta = !escolhido ? 'Escolha o status.' : (exigeObservacao && !obs.texto.trim() ? 'Este status pede a observação.' : '');

    const gravar = async () => {
        if (falta) {
            toast.warning(falta);
            return;
        }
        setGravando(true);
        try {
            const res = await api({
                method: 'post', action: '/eventoBackofficeStatus', token,
                data: {
                    evento: evento.id,
                    status: escolhido,
                    observacao: obs.texto,
                    // A entrada é nova: os anexos vão todos com o arquivo, na ordem da tela.
                    anexos: obs.anexos.filter((a) => a.arquivo instanceof File).map((a) => ({ arquivo: a.arquivo, linha: a.linha })),
                },
            });
            toast.success(res.data?.data?.mensagem || 'Status do Backoffice alterado');
            if (sugereFechar && fecharTambem) {
                try {
                    await api({ method: 'put', action: '/evento/fechar/' + evento.id, token,
                        data: { status: 'fechado', dataFechamento: moment().format('YYYY-MM-DD') } });
                    toast.success('Fechamento do Backoffice registrado');
                } catch (err) {
                    notify.apiError(err, 'O status foi gravado, mas o Fechar do Backoffice não passou');
                }
            }
            onGravado();
        } catch (err) {
            notify.apiError(err, 'Erro ao mudar o status do Backoffice');
        } finally {
            setGravando(false);
        }
    };

    return (
        <div className="modal fade show d-block fin-pp fin-pp__modalbg"
            onClick={(e) => { if (e.target === e.currentTarget && !gravando) onFechar(); }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content fin-pp__modal">
                    <div className="fin-pp__modal-head">
                        <span className="fin-pp__modal-head-ic"><FaExchangeAlt size={17} /></span>
                        <h3 className="fin-pp__modal-title">Mudar status do Backoffice
                            <span className="fin-pp__modal-sub">{[evento.codigo, evento.nome].filter(Boolean).join(' · ')}</span>
                        </h3>
                        <button type="button" className="fin-pp__modal-close" onClick={onFechar} disabled={gravando}><FaTimes size={16} /></button>
                    </div>
                    <div className="modal-body">
                        {!historico ? (
                            <div className="fin-pp__cell-muted">Carregando...</div>
                        ) : (
                            <div className="fin-pp__filters">
                                <FinField label="Status atual" full>
                                    <span><PillStatus status={historico.atual} /></span>
                                </FinField>
                                <FinField label="Novo status" required full>
                                    <FinChecks>
                                        {Object.entries(historico.status).map(([codigo, rotulo]) => (
                                            <FinCheck key={codigo} id={`statusBackoffice-${codigo}`} type="radio" name="statusBackoffice"
                                                label={rotulo} checked={escolhido === codigo} onChange={() => setEscolhido(codigo)} />
                                        ))}
                                    </FinChecks>
                                </FinField>
                                <FinField label={exigeObservacao ? 'Observação do Backoffice' : 'Observação'} required={exigeObservacao} full>
                                    <ObservacaoAnexos value={obs} onChange={setObs} accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                                        placeholder={exigeObservacao ? 'O que falta ou a divergência. Cole (Ctrl+V) ou arraste o print, imagem ou PDF.' : 'Observação, se precisar.'} />
                                </FinField>
                                {sugereFechar && (
                                    <FinField full>
                                        <FinCheck id="statusBackofficeFechar" label="Fechar o Backoffice também (o Fechar do evento, com a data de hoje)"
                                            checked={fecharTambem} onChange={(ev) => setFecharTambem(ev.target.checked)} />
                                    </FinField>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="fin-pp__modal-foot">
                        <button type="button" className="fin-pp__btn" onClick={onFechar} disabled={gravando}>Cancelar</button>
                        <button type="button" className="fin-pp__btn fin-pp__btn--primary" onClick={gravar} disabled={gravando || !historico}>
                            {gravando ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/** O histórico do status do Backoffice de um evento: cada entrada, com quem, quando, de onde veio e a observação. */
function HistoricoStatusModal({ evento, token, onFechar }) {
    const [historico, setHistorico] = useState(null);

    useEffect(() => {
        api({ method: 'get', action: '/eventoBackofficeStatus', token, params: { evento: evento.id } })
            .then((res) => setHistorico(res.data?.data ?? null))
            .catch((err) => notify.apiError(err, 'Erro ao carregar o histórico do Backoffice'));
    }, [evento.id, token]);

    const anexosDe = (entrada) => (entrada.anexos || []).map((anexo) => ({
        id: anexo.id,
        nome: anexo.nome,
        link: `${API}/arquivo/download/${anexo.arquivo}`,
        linha: anexo.linha ?? null,
    }));

    return (
        <div className="modal fade show d-block fin-pp fin-pp__modalbg" onClick={(e) => { if (e.target === e.currentTarget) onFechar(); }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content fin-pp__modal">
                    <div className="fin-pp__modal-head">
                        <span className="fin-pp__modal-head-ic"><FaHistory size={17} /></span>
                        <h3 className="fin-pp__modal-title">Histórico do Backoffice
                            <span className="fin-pp__modal-sub">{[evento.codigo, evento.nome].filter(Boolean).join(' · ')}</span>
                        </h3>
                        <button type="button" className="fin-pp__modal-close" onClick={onFechar}><FaTimes size={16} /></button>
                    </div>
                    <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        {!historico ? (
                            <div className="fin-pp__cell-muted">Carregando...</div>
                        ) : historico.entradas.length === 0 ? (
                            <div className="fin-pp__cell-muted">
                                Nenhuma mudança registrada. O status de agora, <PillStatus status={historico.atual} />, sai do evento.
                            </div>
                        ) : (
                            <div className="fin-pp__tblwrap">
                                <table className="fin-pp__tbl">
                                    <thead>
                                        <tr><th style={{ width: 210 }}>Status</th><th style={{ width: 200 }}>Quem e quando</th><th>Observação</th></tr>
                                    </thead>
                                    <tbody>
                                        {historico.entradas.map((entrada) => (
                                            <tr key={entrada.id}>
                                                <td>
                                                    <PillStatus status={entrada} />
                                                    <div className="fin-pp__cell-muted" style={{ fontSize: 12, marginTop: 4 }}>{ORIGEM[entrada.origem] || entrada.origem}</div>
                                                </td>
                                                <td className="fin-pp__cell-muted">
                                                    <div className="fin-pp__cell-strong">{entrada.registrado_por?.nome || '-'}</div>
                                                    {quando(entrada.registrado_em)}
                                                </td>
                                                <td>
                                                    {entrada.observacao || (entrada.anexos || []).length
                                                        ? <ObservacaoAnexos readOnly semCaixa value={{ texto: entrada.observacao || '', anexos: anexosDe(entrada) }} />
                                                        : <span className="fin-pp__cell-muted">—</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    <div className="fin-pp__modal-foot">
                        <button type="button" className="fin-pp__btn" onClick={onFechar}>Fechar</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
